(() => {
    const template = document.getElementById('pp-inscription-template');
    if (!template) return;
    const states = new WeakMap();
    const activeStates = new Set();
    const values = new Map();
    const maxLines = 3;
    let scheduled = false;
    const fonts = {
      'Times New Roman': '"Times New Roman", Times, serif',
      'Arial': 'Arial, Helvetica, sans-serif',
      'Segoe UI': '"Segoe UI", Arial, sans-serif',
      'Maven Pro': '"Puffin Inscription Maven Pro", Arial, sans-serif',
      'Felipa': '"Puffin Inscription Felipa", cursive',
      'Courier Prime': '"Puffin Inscription Courier Prime", "Courier New", monospace',
      'Cinzel': '"Puffin Inscription Cinzel", Georgia, serif',
      'Arimo': '"Puffin Inscription Arimo", Arial, sans-serif'
    };

    function label(row) {
      return (row?.querySelector('.mw-text--style-strong')?.textContent || '').trim().toLowerCase();
    }

    function fontFor(row) {
      const container = row.closest('.mw-apo-container');
      return Array.from(container?.querySelectorAll('.mw-option') || []).find(item => label(item) === 'inscription font')?.querySelector('select');
    }

    function normaliseLines(text) {
      return text.replace(/\r\n?/g, '\n');
    }

    function limitLines(text) {
      return normaliseLines(text).split('\n').slice(0, maxLines).join('\n');
    }

    function sync(state) {
      state.font = fontFor(state.row);
      const { editor, source, preview, font } = state;
      if (font) {
        font.classList.add('pp-inscription-font-select');
        font.closest('.mw-option').classList.add('pp-inscription-native-font');
      }
      state.selectedFontValue = font?.value || '';
      if (editor !== source) {
        editor.disabled = source.disabled;
        editor.readOnly = source.readOnly;
      }
      const text = editor.value;
      const textNode = preview.querySelector('.pp-inscription-preview__text');
      if (textNode.textContent !== text) textNode.textContent = text;
      const selectedFont = font?.selectedOptions[0]?.textContent.trim();
      const family = fonts[selectedFont] || fonts.Arial;
      if (textNode.style.fontFamily !== family) textNode.style.fontFamily = family;
      const plaque = preview.querySelector('.pp-inscription-preview__plaque');
      const description = text.trim() || preview.querySelector('.pp-inscription-preview__placeholder').textContent;
      if (plaque.getAttribute('aria-label') !== description) plaque.setAttribute('aria-label', description);
    }

    function mount(row) {
      const existing = states.get(row);
      if (existing?.editor.isConnected && existing.source.isConnected && existing.preview.isConnected) {
        sync(existing);
        existing.remember();
        return;
      }
      const source = row.querySelector('input.mw-option__control:not([type="checkbox"]):not([type="radio"]), textarea.mw-option__control');
      if (!source) return;
      const container = row.closest('.mw-apo-container');
      const fontRow = Array.from(container.querySelectorAll('.mw-option')).find(item => label(item) === 'inscription font');
      const font = fontRow?.querySelector('select');
      const key = (row.closest('form')?.id || location.pathname) + ':' + source.name;
      const saved = values.get(key);
      row.querySelectorAll('.pp-inscription-editor, .pp-inscription-help').forEach(item => {
        if (item !== source) item.remove();
      });
      (fontRow || row).querySelectorAll('.pp-inscription-preview').forEach(item => item.remove());
      const help = template.content.querySelector('.pp-inscription-help').cloneNode(true);
      const preview = template.content.querySelector('.pp-inscription-preview').cloneNode(true);
      const editor = source.tagName === 'TEXTAREA' ? source : document.createElement('textarea');
      if (editor !== source) {
        editor.id = source.id + '-multiline';
        editor.value = source.value;
        editor.required = source.required;
        if (source.hasAttribute('maxlength')) editor.maxLength = source.maxLength;
        if (source.hasAttribute('minlength')) editor.minLength = source.minLength;
        if (source.hasAttribute('form')) editor.setAttribute('form', source.getAttribute('form'));
        ['aria-label', 'aria-labelledby', 'aria-describedby', 'aria-required', 'placeholder'].forEach(name => {
          if (source.hasAttribute(name)) editor.setAttribute(name, source.getAttribute(name));
        });
        source.type = 'hidden';
        source.after(editor);
        row.querySelectorAll('label[for]').forEach(item => {
          if (item.htmlFor === source.id) item.htmlFor = editor.id;
        });
      }
      editor.classList.add('pp-inscription-editor');
      editor.rows = maxLines;
      if (saved) {
        editor.value = saved.text;
        source.value = saved.text;
      }
      editor.value = limitLines(editor.value);
      source.value = editor.value;
      help.id = editor.id + '-help';
      const describedBy = new Set((editor.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      describedBy.add(help.id);
      editor.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
      editor.after(help);
      (fontRow || row).append(preview);
      const warning = help.querySelector('.pp-inscription-limit');
      const state = { row, editor, source, preview, font, acceptedText: editor.value };
      states.set(row, state);
      activeStates.add(state);
      const remember = () => values.set(key, { text: editor.value, font: state.font?.value || '' });
      state.remember = remember;
      const checkInsertion = (event, text) => {
        const next = editor.value.slice(0, editor.selectionStart) + normaliseLines(text) + editor.value.slice(editor.selectionEnd);
        if (next.split('\n').length <= maxLines) return;
        event.preventDefault();
        warning.hidden = false;
      };
      editor.addEventListener('beforeinput', event => {
        if (event.isComposing) return;
        if (event.inputType === 'insertLineBreak' || event.inputType === 'insertParagraph') {
          checkInsertion(event, '\n');
        } else if (event.data) {
          checkInsertion(event, event.data);
        }
      });
      editor.addEventListener('paste', event => {
        if (event.clipboardData) checkInsertion(event, event.clipboardData.getData('text/plain'));
      });
      ['input', 'change'].forEach(eventName => {
        editor.addEventListener(eventName, () => {
          const text = normaliseLines(editor.value);
          if (text.split('\n').length > maxLines) {
            editor.value = state.acceptedText;
            warning.hidden = false;
          } else {
            editor.value = text;
            state.acceptedText = text;
            warning.hidden = true;
          }
          if (editor !== source) {
            source.value = editor.value;
            source.dispatchEvent(new Event(eventName, { bubbles: true }));
          }
          remember();
          sync(state);
        });
      });
      font?.addEventListener('change', () => {
        sync(state);
        remember();
      });
      if (editor !== source) source.addEventListener('change', () => {
        if (editor.value !== source.value) editor.value = limitLines(source.value);
        source.value = editor.value;
        state.acceptedText = editor.value;
        remember();
        sync(state);
      });
      if (saved) {
        source.dispatchEvent(new Event('input', { bubbles: true }));
        source.dispatchEvent(new Event('change', { bubbles: true }));
        if (font && Array.from(font.options).some(option => option.value === saved.font)) {
          font.value = saved.font;
          font.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      sync(state);
    }

    function scan() {
      scheduled = false;
      document.querySelectorAll('.mw-apo-container .mw-option').forEach(row => {
        if (/^inscription plaque(?: text)?$/.test(label(row))) mount(row);
      });
    }

    function scheduleScan() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(scan);
    }

    ['input', 'change', 'click', 'keyup'].forEach(eventName => {
      document.addEventListener(eventName, event => {
        if (label(event.target.closest?.('.mw-option')) === 'inscription font') scheduleScan();
      }, true);
    });

    const observer = new MutationObserver(mutations => {
      if (!mutations.some(mutation => {
        const target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
        if (label(target?.closest('.mw-option')) === 'inscription font') return true;
        return Array.from(mutation.addedNodes).some(node =>
          node.nodeType === 1 && (node.matches('.mw-apo-container, .mw-option, .mw-option__control') || node.querySelector('.mw-apo-container, .mw-option, .mw-option__control'))
        );
      })) return;
      scheduleScan();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['selected', 'aria-selected', 'value'] });
    setInterval(() => {
      if (document.hidden) return;
      activeStates.forEach(state => {
        if (!state.row.isConnected || !state.editor.isConnected) {
          activeStates.delete(state);
          return;
        }
        if (!state.preview.isConnected) {
          activeStates.delete(state);
          scheduleScan();
          return;
        }
        const font = state.font?.isConnected ? state.font : fontFor(state.row);
        if (font !== state.font || (font?.value || '') !== state.selectedFontValue) {
          sync(state);
          state.remember();
        }
      });
    }, 200);
    document.addEventListener('shopify:section:load', scan);
    document.addEventListener('reset', () => requestAnimationFrame(() => {
      document.querySelectorAll('.mw-apo-container .mw-option').forEach(row => {
        const state = states.get(row);
        if (!state) return;
        if (state.editor !== state.source) state.editor.value = state.source.value;
        sync(state);
      });
    }));
    scan();
  })();
